// apps/merchant-v3/src/services/billingClient.ts
//
// Phase 6 Wave 10 Task 32 — Thin wrapper over `apiClient` for the new
// merchant Billing module (Amendment #1: Invoices = standalone
// Finance entry, NOT a Wallet sub-tab). Read-only endpoints exercised
// by `BillingOverviewPage` + `InvoicesListPage` in this task. Detail /
// create / pay endpoints are intentionally stubbed as TODOs for
// Tasks 33 + 34 so this client stays scoped.
//
//   GET /api/v1/merchants/me/billing-summary  — KPI snapshot + mode
//   GET /api/v1/merchants/me/invoices         — paginated invoice list
//
// Phase 6 Wave 10 Task 33 — Detail / create / pay / payment history /
// statements added. New endpoints exercised by InvoiceDetailPage,
// InvoiceCreatePage, InvoicePaymentPage, PaymentHistoryPage and
// StatementsPage:
//
//   GET  /api/v1/merchants/me/invoices/:id            — invoice detail
//   POST /api/v1/merchants/me/invoices                — create invoice
//   GET  /api/v1/merchants/me/invoices/:id/pdf        — PDF Blob
//   POST /api/v1/merchants/me/invoices/:id/pay        — pay (Idempotency-Key)
//   GET  /api/v1/merchants/me/payment-history         — paginated history
//   GET  /api/v1/merchants/me/statements              — period aggregation
//
// Phase 6 Wave 10 Task 34 — Payment methods + Fulfillment reports added.
// New endpoints exercised by PaymentMethodsPage, FulfillmentReportsPage,
// FulfillmentReportsGeneratePage and AddPaymentMethodPopup:
//
//   GET    /api/v1/merchants/me/payment-methods             — saved Stripe/PayPal
//   POST   /api/v1/merchants/me/payment-methods             — add new method
//   DELETE /api/v1/merchants/me/payment-methods/:id         — remove method
//   POST   /api/v1/merchants/me/payment-methods/:id/set-default — pin default
//   GET    /api/v1/merchants/me/fulfillment-reports         — list reports
//   POST   /api/v1/merchants/me/fulfillment-reports/generate — kick off async gen
//   GET    /api/v1/merchants/me/fulfillment-reports/:id     — single status poll
//
// Decisions covered:
//     (Payment methods + fulfillment reports surface, Goal)
//     (Statements aggregation, period select)
//    (overview KPI shape), (sidebar badge live data).
//
// vg-binding: PLAN:task-32
// vg-binding: PLAN:task-33
// vg-binding: PLAN:task-34
// vg-binding: P6.D-66
// vg-binding: P6.D-70
// vg-binding: P6.D-245
// vg-binding: P6.D-247
// vg-binding: G-52
// vg-binding: G-53
// vg-binding: G-54
// vg-binding: G-55
// vg-binding: G-56
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import { apiClient, API_BASE_URL, ApiError } from '@/lib/api/client';
import type {
  AddPaymentMethodPayload,
  BillingSummaryV2,
  CreateInvoicePayload,
  FulfillmentReport,
  FulfillmentReportStatus,
  FulfillmentReportsFilters,
  FulfillmentReportsResult,
  GenerateFulfillmentReportPayload,
  GenerateFulfillmentReportResponse,
  Invoice,
  InvoiceDetail,
  InvoiceLineItem,
  InvoiceListFilters,
  InvoiceListResult,
  InvoicePaymentMethod,
  CardPaymentPermission,
  CardPaymentPurpose,
  PayInvoiceResponse,
  PaymentHistoryFilters,
  PaymentHistoryResult,
  PaymentHistoryRow,
  SavedPaymentMethod,
  SetupIntentResponse,
  Statement,
  StatementsFilters,
  StatementsResult,
  StripeConfig,
} from '@/types/billing';

const BASE = '/api/v1/merchants/me';

interface WalletSummaryWire {
  balance_usd_cents?: number;
  held_usd_cents?: number;
  available_usd_cents?: number;
  is_frozen?: boolean;
}

interface InvoiceSummaryWire {
  net_outstanding_usd_cents?: number;
  gross_outstanding_usd_cents?: number;
  overdue_count?: number;
  next_due_at?: string | null;
}

interface BillingSummaryViewWire {
  billing_mode?: BillingSummaryV2['billing_mode'];
  wallet?: WalletSummaryWire | null;
  invoice_summary?: InvoiceSummaryWire | null;
}

type InvoiceStatusWire = Invoice['status'] | 'issued' | 'pending_approval';

interface InvoiceRowWire {
  id?: string;
  invoiceNumber?: string;
  invoice_number?: string;
  status?: InvoiceStatusWire;
  issueDate?: string;
  issued_at?: string | null;
  period_start?: string;
  dueDate?: string;
  due_at?: string | null;
  period_end?: string;
  amount?: number;
  total?: number;
  currency?: string;
  customerName?: string;
  legal_entity_name?: string;
  store_name?: string | null;
}

interface InvoiceDetailWire extends InvoiceRowWire {
  customerEmail?: string;
  billing_address?: {
    name?: string;
    email?: string;
  };
  lineItems?: InvoiceLineItem[];
  line_items?: Array<{
    description?: string;
    quantity?: number;
    unit_price?: number;
    line_total?: number;
  }>;
  subtotal?: number;
  credit_applied?: number;
  taxRate?: number;
  taxAmount?: number;
  tax_total?: number;
  tax_breakdown?: Array<{ rate?: number; amount?: number }>;
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function centsToMajor(value: unknown): number {
  return toFiniteNumber(value) / 100;
}

function moneyFromWire(value: unknown, isCentWire: boolean): number {
  return isCentWire ? centsToMajor(value) : toFiniteNumber(value);
}

function normalizeInvoiceStatus(value: unknown): Invoice['status'] {
  if (value === 'paid' || value === 'draft' || value === 'cancelled') {
    return value;
  }
  if (value === 'overdue') return 'overdue';
  return 'pending';
}

function normalizeInvoiceRow(raw: Invoice | InvoiceRowWire): Invoice {
  const wire = raw as InvoiceRowWire;
  const snakeWire =
    'invoice_number' in wire ||
    'period_start' in wire ||
    'issued_at' in wire;
  return {
    id: String(wire.id ?? ''),
    invoiceNumber: String(wire.invoiceNumber ?? wire.invoice_number ?? ''),
    status: normalizeInvoiceStatus(wire.status),
    issueDate: String(
      wire.issueDate ?? wire.issued_at ?? wire.period_start ?? '',
    ),
    dueDate: String(wire.dueDate ?? wire.due_at ?? wire.period_end ?? ''),
    amount: moneyFromWire(wire.amount ?? wire.total, snakeWire),
    currency: wire.currency ?? 'USD',
    customerName:
      wire.customerName ?? wire.store_name ?? wire.legal_entity_name ?? undefined,
  };
}

function normalizeInvoiceDetail(
  raw: InvoiceDetail | InvoiceDetailWire,
): InvoiceDetail {
  const wire = raw as InvoiceDetailWire;
  const snakeWire =
    'invoice_number' in wire ||
    'line_items' in wire ||
    'billing_address' in wire;
  const row = normalizeInvoiceRow(wire);
  const lineItems = Array.isArray(wire.lineItems)
    ? wire.lineItems
    : Array.isArray(wire.line_items)
      ? wire.line_items.map((li) => ({
          description: li.description ?? '',
          qty: toFiniteNumber(li.quantity, 1),
          unitPrice: moneyFromWire(li.unit_price, true),
          lineTotal: moneyFromWire(li.line_total, true),
        }))
      : [];
  const taxLine = Array.isArray(wire.tax_breakdown)
    ? wire.tax_breakdown[0]
    : undefined;
  return {
    ...row,
    customerName:
      wire.customerName ?? wire.billing_address?.name ?? row.customerName,
    customerEmail: wire.customerEmail ?? wire.billing_address?.email,
    lineItems,
    subtotal: moneyFromWire(wire.subtotal, snakeWire),
    creditApplied: moneyFromWire(wire.credit_applied, snakeWire),
    taxRate: toFiniteNumber(wire.taxRate ?? taxLine?.rate),
    taxAmount: moneyFromWire(wire.taxAmount ?? wire.tax_total ?? taxLine?.amount, snakeWire),
    total: moneyFromWire(wire.total ?? row.amount, snakeWire),
  };
}

function isDueSoon(nextDueAt: string | null | undefined): boolean {
  if (!nextDueAt) return false;
  const due = Date.parse(nextDueAt);
  if (!Number.isFinite(due)) return false;
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return due >= now && due <= now + sevenDaysMs;
}

function normalizeBillingSummary(
  raw: BillingSummaryV2 | BillingSummaryViewWire,
): BillingSummaryV2 {
  if (
    raw &&
    typeof raw === 'object' &&
    ('wallet_balance' in raw || 'outstanding' in raw)
  ) {
    const flat = raw as BillingSummaryV2;
    return {
      wallet_balance: toFiniteNumber(flat.wallet_balance),
      outstanding: toFiniteNumber(flat.outstanding),
      overdue_count: toFiniteNumber(flat.overdue_count),
      due_soon_count: toFiniteNumber(flat.due_soon_count),
      billing_mode: flat.billing_mode ?? 'PREPAID',
      currency: flat.currency ?? 'USD',
      lastUpdated: flat.lastUpdated ?? new Date().toISOString(),
    };
  }

  const view = raw as BillingSummaryViewWire;
  const invoice = view.invoice_summary ?? null;
  const wallet = view.wallet ?? null;
  const overdueCount = toFiniteNumber(invoice?.overdue_count);

  return {
    wallet_balance: centsToMajor(
      wallet?.available_usd_cents ?? wallet?.balance_usd_cents,
    ),
    outstanding: centsToMajor(
      invoice?.net_outstanding_usd_cents ?? invoice?.gross_outstanding_usd_cents,
    ),
    overdue_count: overdueCount,
    due_soon_count:
      overdueCount > 0 ? 0 : isDueSoon(invoice?.next_due_at) ? 1 : 0,
    billing_mode: view.billing_mode ?? 'PREPAID',
    currency: 'USD',
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * GET /api/v1/merchants/me/billing-summary — aggregated KPI snapshot
 * (outstanding amount, overdue count, due-soon count, wallet balance,
 * billing mode). 404 / NETWORK_ERROR fallback returns a zero-summary
 * shape with PREPAID mode so first paint never crashes for brand-new
 * merchants whose billing row has not yet been materialized.
 */
export async function getBillingSummary(): Promise<BillingSummaryV2> {
  try {
    const data = await apiClient.get<
      | BillingSummaryV2
      | BillingSummaryViewWire
      | { summary?: BillingSummaryV2 | BillingSummaryViewWire }
    >(`${BASE}/billing-summary`);
    if (data && typeof data === 'object' && 'summary' in data && data.summary) {
      return normalizeBillingSummary(data.summary);
    }
    return normalizeBillingSummary(
      data as BillingSummaryV2 | BillingSummaryViewWire,
    );
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return {
        wallet_balance: 0,
        outstanding: 0,
        overdue_count: 0,
        due_soon_count: 0,
        billing_mode: 'PREPAID',
        currency: 'USD',
        lastUpdated: new Date().toISOString(),
      };
    }
    throw err;
  }
}

/**
 * `YYYY-MM-DD` (local) → the ISO instant the invoices API validates against.
 *
 * Two things the server is strict about: it rejects a bare date, and it
 * compares the upper bound with `$lte` against a stored timestamp. So the
 * start of the window is local midnight and the end is the last millisecond
 * of the chosen day — otherwise picking "to: 30 Sep" returns nothing dated
 * 30 Sep.
 *
 * Local midnight, not UTC: the merchant picked a day on a calendar, and
 * every other date filter in this app reads the same way.
 */
function ymdToIso(ymd: string | undefined, endOfDay: boolean): string | undefined {
  if (!ymd) return undefined;
  const d = new Date(`${ymd}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

const ymdToIsoStart = (ymd: string | undefined): string | undefined =>
  ymdToIso(ymd, false);
const ymdToIsoEnd = (ymd: string | undefined): string | undefined =>
  ymdToIso(ymd, true);

/**
 * GET /api/v1/merchants/me/invoices — paginated, filterable list of the
 * merchant's invoices. Filters are forwarded as query params; the BE
 * applies sensible defaults (issueDate DESC, page=1, limit=25). 404 /
 * NETWORK_ERROR fallback returns an empty page so the table renders
 * its empty state instead of crashing.
 */
export async function listInvoices(
  filters: InvoiceListFilters = {},
): Promise<InvoiceListResult> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.q && filters.q.trim().length > 0) qs.set('q', filters.q.trim());
  // The API names these `date_from`/`date_to` and validates them as full ISO
  // instants — a bare `YYYY-MM-DD` is rejected outright. The upper bound is
  // pushed to the END of the chosen day because the server compares with
  // `$lte`: sending midnight would silently drop everything dated that day,
  // i.e. the day the merchant just picked.
  const from = ymdToIsoStart(filters.from);
  const to = ymdToIsoEnd(filters.to);
  if (from) qs.set('date_from', from);
  if (to) qs.set('date_to', to);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.limit) qs.set('limit', String(filters.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<
      InvoiceListResult | { invoices?: Invoice[]; total?: number }
    >(`${BASE}/invoices${suffix}`);
    if (
      data &&
      typeof data === 'object' &&
      'rows' in data &&
      Array.isArray((data as InvoiceListResult).rows)
    ) {
      const d = data as InvoiceListResult;
      return { rows: d.rows.map(normalizeInvoiceRow), total: d.total };
    }
    if (
      data &&
      typeof data === 'object' &&
      'invoices' in data &&
      Array.isArray((data as { invoices?: Invoice[] }).invoices)
    ) {
      const d = data as { invoices: Invoice[]; total?: number };
      return {
        rows: d.invoices.map(normalizeInvoiceRow),
        total: d.total ?? d.invoices.length,
      };
    }
    if (
      data &&
      typeof data === 'object' &&
      'data' in data &&
      Array.isArray((data as { data?: InvoiceRowWire[] }).data)
    ) {
      const d = data as {
        data: InvoiceRowWire[];
        pagination?: {
          total?: number;
          page?: number;
          limit?: number;
          total_pages?: number;
        };
      };
      const total = d.pagination?.total ?? d.data.length;
      const limit = d.pagination?.limit;
      const page = d.pagination?.page;
      const page_count =
        d.pagination?.total_pages ??
        (typeof limit === 'number' && limit > 0
          ? Math.ceil(total / limit)
          : undefined);
      return {
        rows: d.data.map(normalizeInvoiceRow),
        total,
        ...(page !== undefined ? { page } : {}),
        ...(limit !== undefined ? { limit } : {}),
        ...(page_count !== undefined ? { page_count } : {}),
      };
    }
    return { rows: [], total: 0 };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

// --- Task 33 — invoice detail / create / pay / history / statements ----

/** Minimal hand-rolled fallback so the detail page renders cleanly while
 *  the BE endpoint is still being wired. Total/subtotal default to 0;
 *  the page surfaces a "Loading…" / empty state when this returns. */
function emptyInvoiceDetail(id: string): InvoiceDetail {
  return {
    id,
    invoiceNumber: id,
    status: 'pending',
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date().toISOString().slice(0, 10),
    amount: 0,
    currency: 'USD',
    lineItems: [],
    subtotal: 0,
    taxRate: 0,
    taxAmount: 0,
    total: 0,
  };
}

/**
 * GET /api/v1/merchants/me/invoices/:id — full invoice detail. The BE may
 * return either a naked InvoiceDetail (`{ ok:true, data: InvoiceDetail }`)
 * or a wrapped form `{ invoice: InvoiceDetail }`; we accept both shapes
 * so the FE keeps rendering cleanly across BE shape drift. 404 / network
 * fallback returns an empty detail so the page surfaces the empty state
 * instead of crashing.
 */
export async function getInvoice(id: string): Promise<InvoiceDetail> {
  try {
    const data = await apiClient.get<
      InvoiceDetail | { invoice?: InvoiceDetail }
    >(`${BASE}/invoices/${encodeURIComponent(id)}`);
    if (data && typeof data === 'object' && 'invoice' in data && data.invoice) {
      return normalizeInvoiceDetail(data.invoice);
    }
    return normalizeInvoiceDetail(data as InvoiceDetail | InvoiceDetailWire);
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return emptyInvoiceDetail(id);
    }
    throw err;
  }
}

/**
 * POST /api/v1/merchants/me/invoices — create a draft / pending invoice.
 * Server returns the new invoice id + invoice number which the page uses
 * to navigate into `/billing/invoices/${id}`.
 */
export async function createInvoice(
  payload: CreateInvoicePayload,
): Promise<{ invoice: { id: string; invoiceNumber: string } }> {
  const data = await apiClient.post<
    { invoice: { id: string; invoiceNumber: string } } | { id: string; invoiceNumber: string }
  >(`${BASE}/invoices`, payload);
  if (data && typeof data === 'object' && 'invoice' in data && data.invoice) {
    return { invoice: data.invoice };
  }
  const flat = data as { id: string; invoiceNumber: string };
  return { invoice: { id: flat.id, invoiceNumber: flat.invoiceNumber } };
}

/**
 * GET /api/v1/merchants/me/invoices/:id/pdf — returns a PDF Blob. Caller
 * is expected to wrap with `URL.createObjectURL(blob)` and `window.open`
 * to print/preview in a new tab. Routed via raw fetch so we keep the
 * cookie + CSRF behaviour while bypassing the JSON envelope unwrap
 * (binary download).
 */
export async function getInvoicePdf(id: string): Promise<Blob> {
  const url =
    (API_BASE_URL || '') +
    `${BASE}/invoices/${encodeURIComponent(id)}/pdf`;
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/pdf' },
  });
  if (!res.ok) {
    throw new Error(`Invoice PDF failed: HTTP ${res.status}`);
  }
  return res.blob();
}

/**
 * POST /api/v1/merchants/me/invoices/:id/pay — single endpoint for all
 * 3 methods (Wallet / Stripe / PayPal). The `Idempotency-Key` header is
 * **required**: the BE keys retry dedupe on the same value
 * within a 24 h window. The merchant's `useIdempotencyKey()` hook
 * persists the value into `orderWizardStore` so re-clicks within the
 * same wizard session reuse it.
 */
export function payInvoice(
  id: string,
  method: InvoicePaymentMethod,
  idempotencyKey: string,
  /** REQUIRED for `method:'stripe'` — the saved card to charge. The server
   *  re-resolves it under the merchant's own scope. */
  paymentMethodId?: string,
): Promise<PayInvoiceResponse> {
  return apiClient.post<PayInvoiceResponse>(
    `${BASE}/invoices/${encodeURIComponent(id)}/pay`,
    { method, ...(paymentMethodId ? { paymentMethodId } : {}) },
    { idempotencyKey },
  );
}

/**
 * GET /api/v1/merchants/me/payment-history — paginated, filterable list
 * of all merchant payments. Filters are forwarded as query params; the
 * BE applies sensible defaults (ts DESC, page=1, limit=25). 404 /
 * network fallback returns an empty page so the table renders its empty
 * state instead of crashing.
 */
export async function listPaymentHistory(
  filters: PaymentHistoryFilters = {},
): Promise<PaymentHistoryResult> {
  const qs = new URLSearchParams();
  if (filters.method) qs.set('method', filters.method);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.limit) qs.set('limit', String(filters.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<
      PaymentHistoryResult | { payments?: PaymentHistoryRow[]; total?: number }
    >(`${BASE}/payment-history${suffix}`);
    if (
      data &&
      typeof data === 'object' &&
      'rows' in data &&
      Array.isArray((data as PaymentHistoryResult).rows)
    ) {
      return data as PaymentHistoryResult;
    }
    if (
      data &&
      typeof data === 'object' &&
      'payments' in data &&
      Array.isArray((data as { payments?: PaymentHistoryRow[] }).payments)
    ) {
      const d = data as { payments: PaymentHistoryRow[]; total?: number };
      return { rows: d.payments, total: d.total ?? d.payments.length };
    }
    return { rows: [], total: 0 };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

/**
 * GET /api/v1/merchants/me/statements — period-based aggregation
 * surface. `period` is the period bucket (daily / weekly
 * monthly / custom); `from / to` are only meaningful for custom. The
 * BE returns an array of statements with per-category breakdown that
 * the page renders as a chart + table. 404 / network fallback returns
 * an empty result so the page surfaces the empty state cleanly.
 */
export async function listStatements(
  filters: StatementsFilters,
): Promise<StatementsResult> {
  const qs = new URLSearchParams();
  qs.set('period', filters.period);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<
      StatementsResult | { statements?: Statement[] }
    >(`${BASE}/statements${suffix}`);
    if (
      data &&
      typeof data === 'object' &&
      'rows' in data &&
      Array.isArray((data as StatementsResult).rows)
    ) {
      return data as StatementsResult;
    }
    if (
      data &&
      typeof data === 'object' &&
      'statements' in data &&
      Array.isArray((data as { statements?: Statement[] }).statements)
    ) {
      const d = data as { statements: Statement[] };
      return { rows: d.statements };
    }
    return { rows: [] };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [] };
    }
    throw err;
  }
}

// --- Task 34 — payment methods + fulfillment reports --------------------

/**
 * GET /api/v1/merchants/me/payment-methods — list of saved Stripe / PayPal
 * payment methods. The BE may return either a naked array
 * (`{ ok:true, data: SavedPaymentMethod[] }`) or a wrapped form
 * `{ paymentMethods: SavedPaymentMethod[] }`; we accept both shapes so
 * the FE keeps rendering cleanly across BE shape drift. 404 / network
 * fallback returns an empty array so the page surfaces the empty state
 * cleanly. (.)
 */
export async function listPaymentMethods(): Promise<SavedPaymentMethod[]> {
  try {
    const data = await apiClient.get<
      | SavedPaymentMethod[]
      | { paymentMethods?: SavedPaymentMethod[]; rows?: SavedPaymentMethod[] }
    >(`${BASE}/payment-methods`);
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object' && Array.isArray(data.paymentMethods)) {
      return data.paymentMethods;
    }
    if (data && typeof data === 'object' && Array.isArray(data.rows)) {
      return data.rows;
    }
    return [];
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return [];
    }
    throw err;
  }
}

/**
 * POST /api/v1/merchants/me/payment-methods — register a new Stripe / PayPal
 * payment method. The Stripe path expects the FE to have confirmed a
 * SetupIntent (`stripe.confirmSetup`) and to forward the resulting `pm_…`
 * plus the originating `setupIntentId`; the PayPal path completes via OAuth
 * and forwards the gateway-issued `paypalAccountId`.
 *
 * Card metadata in the response comes from Stripe, read server-side — it is
 * never derived from what was posted. (.)
 */
export async function addPaymentMethod(
  payload: AddPaymentMethodPayload,
): Promise<{ ok: true; paymentMethod: SavedPaymentMethod }> {
  const data = await apiClient.post<
    | { ok: true; paymentMethod: SavedPaymentMethod }
    | { paymentMethod: SavedPaymentMethod }
    | SavedPaymentMethod
  >(`${BASE}/payment-methods`, payload);
  if (data && typeof data === 'object' && 'paymentMethod' in data && data.paymentMethod) {
    return { ok: true, paymentMethod: data.paymentMethod };
  }
  return { ok: true, paymentMethod: data as SavedPaymentMethod };
}

/**
 * GET /api/v1/merchants/me/payment-methods/stripe-config — the platform's
 * Stripe publishable key, so the browser can load Stripe.js.
 *
 * Never throws for "Stripe is off": the BE answers 200 with
 * `configured: false`, and a 404/network failure is mapped to the same
 * shape, so the card tab degrades to a message rather than erroring.
 */
export async function getStripeConfig(): Promise<StripeConfig> {
  try {
    const data = await apiClient.get<StripeConfig>(
      `${BASE}/payment-methods/stripe-config`,
    );
    if (data && typeof data === 'object' && typeof data.configured === 'boolean') {
      return data;
    }
    return { configured: false, publishableKey: null };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { configured: false, publishableKey: null };
    }
    throw err;
  }
}

/**
 * GET /api/v1/merchants/me/payment-methods/card-payment — whether this
 * merchant may pay orders/invoices directly with a saved card.
 *
 * A per-merchant whitelist an admin opens, so the answer is unknowable from
 * the client. Asked BEFORE rendering the card option, so a merchant who is
 * not whitelisted never sees a choice that would be refused on submit.
 *
 * Fails CLOSED: any error (404 on an older API, offline, a malformed body)
 * resolves to "not allowed". Guessing `true` here would show a payment option
 * whose submit is a guaranteed refusal.
 *
 * `purpose` is not cosmetic: an INVOICE-billed merchant may settle an INVOICE
 * by card but never an individual ORDER, so asking with the wrong purpose
 * hides the option from merchants the charge path actually permits.
 */
export async function getCardPaymentPermission(
  purpose: CardPaymentPurpose = 'order',
): Promise<CardPaymentPermission> {
  const CLOSED: CardPaymentPermission = {
    enabled: false,
    maxPerOrderCents: null,
    maxPerDayCents: null,
  };
  try {
    const data = await apiClient.get<CardPaymentPermission>(
      `${BASE}/payment-methods/card-payment?purpose=${purpose}`,
    );
    if (data && typeof data === 'object' && typeof data.enabled === 'boolean') {
      return data;
    }
    return CLOSED;
  } catch (err) {
    // Closed on failure, deliberately: offering a card the server may refuse
    // is worse than hiding it. Logged so a 500 here is not mistaken for "not
    // whitelisted" while debugging.
    console.warn('[card-payment] permission check failed — hiding the card option', err);
    return CLOSED;
  }
}

/**
 * POST /api/v1/merchants/me/payment-methods/setup-intent — mint a
 * SetupIntent for the Payment Element to confirm against.
 *
 * Called ONLY after `elements.submit()` passes, so an invalid card never
 * creates a Stripe object. Errors propagate: the caller needs to tell the
 * merchant that setup could not start.
 */
export async function createSetupIntent(): Promise<SetupIntentResponse> {
  const data = await apiClient.post<SetupIntentResponse>(
    `${BASE}/payment-methods/setup-intent`,
    {},
  );
  if (!data || typeof data.clientSecret !== 'string' || !data.clientSecret) {
    throw new Error('setup-intent response missing clientSecret');
  }
  return data;
}

/**
 * DELETE /api/v1/merchants/me/payment-methods/:id — remove a saved method.
 * (.)
 */
export async function deletePaymentMethod(
  id: string,
): Promise<{ ok: true }> {
  await apiClient.delete<{ ok?: true } | undefined>(
    `${BASE}/payment-methods/${encodeURIComponent(id)}`,
  );
  return { ok: true };
}

/**
 * POST /api/v1/merchants/me/payment-methods/:id/set-default — pin a saved
 * method as the merchant default. The BE clears any existing default in
 * the same transaction. (.)
 */
export async function setDefaultPaymentMethod(
  id: string,
): Promise<{ ok: true }> {
  await apiClient.post<{ ok?: true } | undefined>(
    `${BASE}/payment-methods/${encodeURIComponent(id)}/set-default`,
  );
  return { ok: true };
}

/**
 * GET /api/v1/merchants/me/fulfillment-reports — paginated list of generated
 * fulfillment reports. Filters are forwarded as query params; the BE applies
 * sensible defaults (generatedAt DESC, page=1, limit=25). 404 / network
 * fallback returns an empty page so the table renders its empty state
 * instead of crashing. (.)
 */
export async function listFulfillmentReports(
  filters: FulfillmentReportsFilters = {},
): Promise<FulfillmentReportsResult> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.limit) qs.set('limit', String(filters.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    const data = await apiClient.get<
      | FulfillmentReportsResult
      | { reports?: FulfillmentReport[]; total?: number }
    >(`${BASE}/fulfillment-reports${suffix}`);
    if (
      data &&
      typeof data === 'object' &&
      'rows' in data &&
      Array.isArray((data as FulfillmentReportsResult).rows)
    ) {
      return data as FulfillmentReportsResult;
    }
    if (
      data &&
      typeof data === 'object' &&
      'reports' in data &&
      Array.isArray((data as { reports?: FulfillmentReport[] }).reports)
    ) {
      const d = data as { reports: FulfillmentReport[]; total?: number };
      return { rows: d.reports, total: d.total ?? d.reports.length };
    }
    return { rows: [], total: 0 };
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { rows: [], total: 0 };
    }
    throw err;
  }
}

/**
 * POST /api/v1/merchants/me/fulfillment-reports/generate — kick off an
 * async report generation. Returns the new report id + initial status; the
 * caller polls `getFulfillmentReportStatus(reportId)` every 3s until the
 * status flips to `'ready'` or `'failed'`. (.)
 */
export async function generateFulfillmentReport(
  payload: GenerateFulfillmentReportPayload,
): Promise<GenerateFulfillmentReportResponse> {
  const data = await apiClient.post<
    | GenerateFulfillmentReportResponse
    | { report?: GenerateFulfillmentReportResponse }
  >(`${BASE}/fulfillment-reports/generate`, payload);
  if (data && typeof data === 'object' && 'report' in data && data.report) {
    return data.report;
  }
  return data as GenerateFulfillmentReportResponse;
}

/**
 * GET /api/v1/merchants/me/fulfillment-reports/:id — single-shot status
 * poll for an in-flight report. Used by the Generate page to drive the 3s
 * polling loop. (.)
 */
export async function getFulfillmentReportStatus(
  id: string,
): Promise<FulfillmentReportStatus> {
  try {
    const data = await apiClient.get<
      FulfillmentReportStatus | { report?: FulfillmentReport }
    >(`${BASE}/fulfillment-reports/${encodeURIComponent(id)}`);
    if (data && typeof data === 'object' && 'report' in data && data.report) {
      return {
        status: data.report.status,
        downloadPdfUrl: data.report.downloadPdfUrl,
        downloadCsvUrl: data.report.downloadCsvUrl,
      };
    }
    return data as FulfillmentReportStatus;
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NETWORK_ERROR')
    ) {
      return { status: 'generating' };
    }
    throw err;
  }
}
